import { useContext, useState } from "react";
import { ButtonDS } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { GetlifeContext } from "../../../../../../context/GetLifeContext.context";
import HttpLeadRepository from "../../../../../../api/request/Lead/Lead.service";
import numberFormat from "../../../../../../utils/Utils";
import {
  ACCENT_SOFT,
  Field,
  inputStyle,
  ModificationModal,
  modalDescStyle,
  modalTitleStyle,
  SummaryRow,
  summaryBoxStyle,
} from "./shared";
import {
  capitalSteps,
  enPalabrasOr,
  resolveCapitalStep,
  resolveMinTraitCapital,
  ruleText,
} from "./capitalRules";
import { PendingCapitalI } from "../../../../../../api/request/Lead/Interfaces/PolicyInterface";
import PendingCapitalLine from "../pendingCapital/PendingCapitalLine";

interface CapitalReductionFlowProps {
  currentCapital: number;
  minCapital: number;
  // gf-monorepo #10195. Ausentes = backend sin las reglas: 5.000 y 10.000.
  capitalStep?: number;
  minTraitCapital?: number;
  capitalsByTrait: Record<string, number> | null;
  // Bajada aprobada que aún no rige: se pinta bajo el capital actual. Crear otra
  // la rechaza el backend (409 PENDING_CAPITAL_CHANGE con enPalabras).
  pendingCapital?: PendingCapitalI | null;
  renewalDate: string | null;
  effectiveDate: string | null;
  onClose: () => void;
  onSubmitted: () => void;
}

// Multi-coverage: the customer decides the capital per coverage. Caps relative
// to DEATH re-validated server-side (DISABILITY ≤5x, SERIOUS_ILLNESS ≤10x).
const TRAIT_ORDER = ["DEATH", "DISABILITY", "SERIOUS_ILLNESS"];

type Step = "form" | "confirm";

// Reglas siempre visibles e inline bajo el campo (en móvil, nada en tooltips).
const hintStyle: React.CSSProperties = {
  fontSize: 13,
  color: "#4b5563",
  lineHeight: 1.5,
  margin: "-6px 0 12px",
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  const [year, month, day] = iso.split("-");
  return `${day}/${month}/${year}`;
};

const CapitalReductionFlow = ({
  currentCapital,
  minCapital,
  capitalStep,
  minTraitCapital,
  capitalsByTrait,
  pendingCapital,
  renewalDate,
  effectiveDate,
  onClose,
  onSubmitted,
}: CapitalReductionFlowProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const { leadId, token, setNotification } = useContext(GetlifeContext);

  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [quoting, setQuoting] = useState(false);
  const [rawCapital, setRawCapital] = useState("");
  const [rawTraitCapitals, setRawTraitCapitals] = useState<Record<string, string>>({});
  const [quote, setQuote] = useState<{
    newAnnualPremium: string | null;
    currentAnnualPremium: string | null;
  } | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const t = (key: string) => translate(`newDashboard.modifications.capital.${key}`);

  const isMulti =
    capitalsByTrait !== null && Object.keys(capitalsByTrait).length > 1;
  const traits = isMulti
    ? TRAIT_ORDER.filter((trait) => capitalsByTrait[trait] !== undefined).concat(
        Object.keys(capitalsByTrait).filter((name) => !TRAIT_ORDER.includes(name))
      )
    : [];

  const stepAmount = resolveCapitalStep(capitalStep);
  const traitMin = resolveMinTraitCapital(minTraitCapital);
  // Una sola garantía: solo se ofrecen los tramos válidos, del actual hacia abajo.
  const steps = isMulti ? [] : capitalSteps(currentCapital, minCapital, stepAmount);

  const traitValue = (trait: string): number =>
    parseInt(rawTraitCapitals[trait] ?? "", 10) || 0;
  const traitValid = (trait: string): boolean => {
    const value = traitValue(trait);
    const current = capitalsByTrait?.[trait] ?? 0;
    if (value <= 0 || value > current) return false;
    if (trait === "DEATH") {
      return value >= Math.max(minCapital, traitMin) && value < current;
    }
    // Multigarantía: ninguna garantía puede quedar por debajo de minTraitCapital.
    return value >= traitMin;
  };
  const traitError = (trait: string): string => {
    const value = traitValue(trait);
    const current = capitalsByTrait?.[trait] ?? 0;
    if (value > 0 && value <= current) {
      if (trait === "DEATH" && value < minCapital) {
        return ruleText(translate, "minRule", { min: minCapital });
      }
      if (value < traitMin) {
        return ruleText(translate, "traitMinRule", { min: traitMin });
      }
    }
    return t("traitInvalid").replace("{max}", numberFormat(current, "capital"));
  };

  const newCapital = isMulti
    ? traitValue("DEATH")
    : parseInt(rawCapital, 10) || 0;
  const newCapitalsByTrait: Record<string, number> | undefined = isMulti
    ? traits.reduce<Record<string, number>>((acc, trait) => {
        acc[trait] = traitValue(trait);
        return acc;
      }, {})
    : undefined;
  const capitalValid = isMulti
    ? traits.every(traitValid) &&
      traits.some((trait) => traitValue(trait) < (capitalsByTrait?.[trait] ?? 0))
    : steps.includes(newCapital);

  // The 10-day rule pushed the effect to the following renewal.
  const deferredToNextYear =
    renewalDate !== null && effectiveDate !== null && renewalDate !== effectiveDate;

  const repository = () => new HttpLeadRepository(leadId, token);

  const requestQuote = async () => {
    if (!capitalValid) return setShowErrors(true);
    if (quoting) return;
    setQuoting(true);
    try {
      const context = await repository().getCapitalReductionContext(
        isMulti ? undefined : newCapital,
        newCapitalsByTrait
      );
      setQuote({
        newAnnualPremium: context.newAnnualPremium,
        currentAnnualPremium: context.currentAnnualPremium,
      });
      setStep("confirm");
    } catch (error) {
      setNotification({
        message: enPalabrasOr(error, translate("newDashboard.modifications.errors.generic")),
        type: "error",
        time: 5,
      });
    } finally {
      setQuoting(false);
    }
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await repository().postModificationRequest(
        "CAPITAL_REDUCTION",
        isMulti ? { capitalsByTrait: newCapitalsByTrait } : { newCapital }
      );
      onSubmitted();
      setNotification({
        message: translate("newDashboard.modifications.common.successToast"),
        type: "success",
        time: 6,
      });
      onClose();
    } catch (error: any) {
      const key =
        error?.status === 409
          ? "newDashboard.modifications.errors.alreadyInProgress"
          : error?.status === 403
            ? "newDashboard.modifications.capital.notEligible"
            : "newDashboard.modifications.errors.generic";
      // 400 con enPalabras (reglas de importe de #10195): la frase del servidor.
      setNotification({ message: enPalabrasOr(error, translate(key)), type: "error", time: 5 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModificationModal onClose={onClose}>
      {step === "form" && (
        <div>
          <h2 style={modalTitleStyle}>{t("title")}</h2>
          <p style={modalDescStyle}>{t("desc")}</p>
          <div style={summaryBoxStyle}>
            <SummaryRow
              label={t("currentCapital")}
              value={numberFormat(currentCapital, "capital")}
            />
            <PendingCapitalLine
              pendingCapital={pendingCapital}
              current={currentCapital}
              style={{ textAlign: "right", margin: "-4px 0 8px" }}
            />
            <SummaryRow label={t("effectiveDate")} value={fmtDate(effectiveDate)} />
          </div>
          {deferredToNextYear && (
            <div
              style={{
                backgroundColor: "#fffbeb",
                border: "1px solid #fef3c7",
                borderRadius: 10,
                padding: "12px 14px",
                marginBottom: 16,
                fontSize: 13,
                color: "#92400e",
                lineHeight: 1.5,
              }}
            >
              {t("tenDayRuleNotice").replace("{date}", fmtDate(effectiveDate))}
            </div>
          )}
          {isMulti ? (
            <>
              {traits.map((trait) => (
                <Field
                  key={trait}
                  label={`${t(`trait.${trait}`)} — ${t("traitCurrent").replace(
                    "{capital}",
                    numberFormat(capitalsByTrait?.[trait] ?? 0, "capital")
                  )}`}
                  error={showErrors && !traitValid(trait) ? traitError(trait) : undefined}
                >
                  <input
                    style={inputStyle}
                    inputMode="numeric"
                    placeholder={String(capitalsByTrait?.[trait] ?? 0)}
                    value={rawTraitCapitals[trait] ?? ""}
                    onChange={(e) =>
                      setRawTraitCapitals({
                        ...rawTraitCapitals,
                        [trait]: e.target.value.replace(/\D/g, ""),
                      })
                    }
                  />
                </Field>
              ))}
              <p style={hintStyle}>
                {ruleText(translate, "traitMinRule", { min: traitMin })}
              </p>
              {showErrors && !capitalValid && traits.every(traitValid) && (
                <p style={{ fontSize: 13, color: "#dc2626", margin: "-6px 0 12px" }}>
                  {t("mustReduceSomething")}
                </p>
              )}
            </>
          ) : (
            <>
              <Field
                label={t("newCapital")}
                error={
                  showErrors && !capitalValid
                    ? 0 === steps.length
                      ? ruleText(translate, "atMinimum", { min: minCapital })
                      : t("capitalInvalid")
                          .replace("{min}", numberFormat(minCapital, "capital"))
                          .replace("{max}", numberFormat(currentCapital, "capital"))
                    : undefined
                }
              >
                {/* Tramos del capital actual hacia abajo: solo se puede elegir un importe válido. */}
                <select
                  style={inputStyle}
                  aria-label={t("newCapital")}
                  value={rawCapital}
                  disabled={0 === steps.length}
                  onChange={(e) => setRawCapital(e.target.value)}
                >
                  <option value="" disabled>
                    —
                  </option>
                  {steps.map((value) => (
                    <option key={value} value={String(value)}>
                      {numberFormat(value, "capital")}
                    </option>
                  ))}
                </select>
              </Field>
              <p style={hintStyle}>{ruleText(translate, "stepRule", { step: stepAmount })}</p>
            </>
          )}
          <p style={{ ...modalDescStyle, fontSize: 13, marginTop: -6 }}>
            {t("stepHint")}
          </p>
          <ButtonDS
            label={t("calculate")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={requestQuote}
            disabled={quoting}
          />
        </div>
      )}

      {step === "confirm" && (
        <div>
          <h2 style={modalTitleStyle}>{t("confirmTitle")}</h2>
          <p style={modalDescStyle}>{t("confirmDesc")}</p>
          <div style={{ ...summaryBoxStyle, backgroundColor: ACCENT_SOFT, borderColor: "transparent" }}>
            {isMulti && newCapitalsByTrait ? (
              traits.map((trait) => (
                <SummaryRow
                  key={trait}
                  label={t(`trait.${trait}`)}
                  value={numberFormat(newCapitalsByTrait[trait] ?? 0, "capital")}
                />
              ))
            ) : (
              <SummaryRow
                label={t("newCapitalShort")}
                value={numberFormat(newCapital, "capital")}
              />
            )}
            {quote?.newAnnualPremium ? (
              <SummaryRow
                label={t("newPremium")}
                value={`${quote.newAnnualPremium}€/año`}
              />
            ) : null}
            {quote?.currentAnnualPremium ? (
              <SummaryRow
                label={t("currentPremium")}
                value={`${quote.currentAnnualPremium}€/año`}
              />
            ) : null}
            <SummaryRow label={t("effectiveDate")} value={fmtDate(effectiveDate)} />
          </div>
          <p style={{ ...modalDescStyle, fontSize: 13 }}>{t("keepUntilRenewal")}</p>
          <ButtonDS
            label={translate("newDashboard.modifications.common.confirmAndSign")}
            buttonType="primary"
            size="40"
            className="w-full"
            onClick={submit}
            disabled={submitting}
          />
          <ButtonDS
            label={translate("newDashboard.modifications.common.back")}
            buttonType="tertiary"
            size="40"
            className="w-full"
            onClick={() => setStep("form")}
          />
        </div>
      )}

    </ModificationModal>
  );
};

export default CapitalReductionFlow;
